"use client";

import Link from "next/link";
import { ArrowRight, TrendingUp, Search, MessageSquare, ShieldCheck, Trophy, Award } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { impact as data } from "@/lib/data/about";

const outcomeIcons = [Search, MessageSquare, ShieldCheck, TrendingUp] as const;

export function Impact() {
  return (
    <Section id="impact" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute bottom-1/3 left-1/3 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.performanceMetrics.map((m, i) => {
            const accents = [
              "var(--color-crimson)",
              "var(--color-purple)",
              "var(--color-cyan)",
              "var(--color-emerald)",
              "var(--color-amber)",
              "var(--color-denim)",
            ] as const;
            const accent = accents[i % accents.length]!;
            return (
              <Reveal key={m.label} delay={i * 60}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <div className="eyebrow-tiny" style={{ color: accent }}>
                    {m.label}
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-4xl font-bold" style={{ color: accent }}>
                      {m.value}
                    </span>
                    <span className="text-sm text-[rgb(var(--fg-muted))]">{m.unit}</span>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="glass rounded-2xl p-6">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Client Outcomes</div>
              <div className="mt-5 space-y-4">
                {data.clientOutcomes.map((c, i) => {
                  const Icon = outcomeIcons[i % outcomeIcons.length]!;
                  return (
                    <div key={c.title} className="flex items-start gap-3">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                        style={{
                          background: "color-mix(in oklch, var(--color-cyan) 18%, transparent)",
                          boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-cyan) 35%, transparent)",
                        }}
                      >
                        <Icon className="h-4 w-4 text-[var(--color-cyan)]" />
                      </span>
                      <div>
                        <div className="font-semibold text-sm">{c.title}</div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{c.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="glass rounded-2xl p-6 h-full">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Industry Impact</div>
              <div className="mt-5 space-y-5">
                {data.industryImpact.map((row, i) => {
                  const Icon = i === 0 ? Trophy : Award;
                  return (
                    <div key={row.heading} className="flex items-start gap-3">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                        style={{
                          background: "color-mix(in oklch, var(--color-amber) 18%, transparent)",
                          boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-amber) 35%, transparent)",
                        }}
                      >
                        <Icon className="h-4 w-4 text-[var(--color-amber)]" />
                      </span>
                      <div>
                        <div className="font-semibold text-sm">{row.heading}</div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-relaxed">{row.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={300}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
